import { useCallback, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { Grid, Typography, Divider } from '@mui/material';
import { CustomField } from '@/components/shared/CustomField';
import * as state from '@/components/Analysis/state';

interface MTProps {
  onFieldValueChange: (value: string, fieldKey: string | undefined | null) => void;
  requiredMTKeys: Set<string>;
}

export const MtComponent = ({ onFieldValueChange, requiredMTKeys }: MTProps) => {
  const allAssemblyTemplates = useAtomValue(state.allAssemblyTemplates);

  const listOfDropdowns = useMemo(
    () =>
      new Set([
        'risk_damage_class',
        'risk_material_type',
        'risk_inspection_method',
        'risk_damage_class_description',
      ]),
    []
  );

  const optionalMTKeys = new Set([
    'risk_elevation',
    'risk_sizing',
    'risk_damage_class_description',
    'risk_damage_mechanism_metric',
    'risk_damage_mechanism_size',
    'risk_additional_remarks',
    'risk_inspection_method',
    'risk_defect_codes',
    'risk_subsea_general_visual_inspection',
    'risk_subsea_ultrasonic_testing',
    'risk_subsea_cathodic_protection',
    'risk_probability_assessment_method',
    'risk_governing_damage_media',
    'risk_inspection_date',
    'risk_member_id',
    'risk_task_description',
    'risk_task_frequency',
    'risk_task_responsibility',
  ]);

  const getFieldType = useCallback(
    (fieldKey: string) => {
      if (listOfDropdowns.has(fieldKey)) return 'SINGLE_SELECT';
      if (fieldKey === 'risk_inspection_date') return 'DATE';
      return 'INPUT';
    },
    [listOfDropdowns]
  );

  return (
    <>
      <Typography sx={{ fontWeight: 'bold' }}>Required fields</Typography>
      <Grid container spacing={2}>
        {allAssemblyTemplates?.length !== undefined && allAssemblyTemplates.length > 0
          ? allAssemblyTemplates?.[0]?.fields
              .filter((field) => typeof field?.key === 'string' && requiredMTKeys.has(field.key))
              .map((field, index) => {
                const fieldId = field.id;
                const fieldOptions = field.options;
                const fieldName = field.name;
                const fieldKey = field?.key;
                const fieldType = fieldKey && getFieldType(fieldKey);

                return (
                  <Grid item xs={index ? 6 : 12} key={fieldId}>
                    <CustomField
                      key={fieldId}
                      fieldName={fieldName}
                      fieldType={fieldType ?? 'INPUT'}
                      fieldOptions={fieldOptions}
                      initialValue=""
                      onChange={(value) => onFieldValueChange(value, fieldKey)}
                    />
                  </Grid>
                );
              })
          : undefined}
      </Grid>

      <Divider sx={{ my: 1 }} />

      <Typography sx={{ fontWeight: 'bold' }}>Optional fields</Typography>
      <Grid container spacing={2}>
        {allAssemblyTemplates?.length !== undefined && allAssemblyTemplates.length > 0
          ? allAssemblyTemplates?.[0]?.fields
              .filter((field) => typeof field?.key === 'string' && optionalMTKeys.has(field.key))
              .map((field) => {
                const fieldId = field.id;
                const fieldOptions = field.options;
                const fieldName = field.name;
                const fieldKey = field.key;
                const fieldType = fieldKey && getFieldType(fieldKey);

                return (
                  <Grid item xs={6} key={fieldId}>
                    <CustomField
                      fieldName={fieldName}
                      fieldType={fieldType ?? 'INPUT'}
                      fieldOptions={fieldOptions}
                      initialValue=""
                      onChange={(value) => onFieldValueChange(value, fieldKey)}
                    />
                  </Grid>
                );
              })
          : undefined}
      </Grid>
    </>
  );
};
